"use client";

import Link from "next/link";
import { Menu } from "lucide-react";
import { usePathname } from "next/navigation";
import { getNavLinks } from "@/components/common/links";
import useResponsive from "@/hooks/useResponsive";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import Logo from "@/components/common/Logo";
import LeaveDetailsDialog from "@/components/forms/leave-details-form/LeaveDetailsFormDialog";
import { useEffect, useMemo, useState } from "react";

type NavbarProps = {
  showGallery?: boolean;
};

const Navbar = ({ showGallery = false }: NavbarProps) => {
  const data = {
    _id: "1",
    header: "ברוכים הבאים ל-Sphog!",
    buttonText: "צרו קשר",
  };

  const pathname = usePathname();
  const { isDesktop } = useResponsive();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setScrolled] = useState(false);
  const navLinks = useMemo(() => getNavLinks(showGallery), [showGallery]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    setScrolled(window.scrollY > 10);

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeSheet = () => {
    setIsOpen(false);
  };

  return (
    <nav
      className={`sticky top-0 w-full h-20 p-5 z-50 flex flex-row-reverse items-center justify-between transition-all duration-100 
        ${isScrolled
          ? "bg-background-overlay backdrop-blur shadow-sm"
          : "bg-transparent"
        }`}
    >
      {/* Logo */}
      <Logo />

      {(!isDesktop) && (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <button
              onClick={() => setIsOpen(true)}
              className="bg-primary text-slate-100 h-10 w-10 p-2 rounded-md"
              aria-label="תפריט ניווט"
            >
              <Menu />
            </button>
          </SheetTrigger>
          <SheetContent className="w-fit bg-white">
            <SheetTitle className="text-primary pt-5 font-bold">{data.header}</SheetTitle>
            <Separator className="my-5" />
            <SheetDescription asChild>
              <div className="space-y-5">
                <ul className="space-y-5">
                  {navLinks.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        aria-label={link.text}
                        onClick={closeSheet} // Close sheet on link click
                        className={`flex gap-2 items-center text-base ${pathname === link.href && "text-primary font-bold"
                          }`}
                      >
                        {link.icon}
                        {link.text}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Separator />
                <LeaveDetailsDialog text={data.buttonText} />
              </div>
            </SheetDescription>
          </SheetContent>
        </Sheet>
      )}
      {isDesktop && (
        <>
          <ul className="flex gap-2">
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  aria-label={link.text}
                  className={`flex items-center justify-center h-10 px-4 rounded-md transition-all duration-300 
                    ${pathname === link.href
                      ? "bg-primary text-white"
                      : "hover:bg-primary-foreground hover:text-white hover:shadow-xl"
                    }`}
                >
                  {link.text}
                </Link>
              </li>
            ))}
          </ul>

          <LeaveDetailsDialog text={data.buttonText} />
        </>
      )}
    </nav>
  );
};

export default Navbar;
